import * as React from 'react';

type ContextType = {
  currentId: string | null;
  registerId: (id: string) => void;
  unregisterId: (id: string) => void;
  generateId: () => string;
};

const DropdownMutexContext = React.createContext<ContextType>({
  currentId: null,
  registerId: () => {},
  unregisterId: () => {},
  generateId: () => '',
});

type Props = {
  children: React.ReactNode;
};

export function DropdownMutexContextProvider({ children }: Props) {
  const [currentId, setCurrentId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const clearCurrentId = () => setCurrentId(null);

    if (currentId) {
      window.addEventListener('click', clearCurrentId);
    }
    return () => {
      if (currentId) {
        window.removeEventListener('click', clearCurrentId);
      }
    };
  }, [currentId]);

  const registerId = React.useCallback((id: string) => setCurrentId(id), []);
  const unregisterId = React.useCallback(
    (id: string) => {
      // should always be the case
      if (id === currentId) {
        setCurrentId(null);
      }
    },
    [currentId]
  );

  const generateId = React.useCallback(() => Math.random().toString(), []);

  return (
    <DropdownMutexContext.Provider
      value={{ currentId, registerId, unregisterId, generateId }}
    >
      {children}
    </DropdownMutexContext.Provider>
  );
}

/**
 * Mutually exclusive handle which can be used in a dropdown component to:
 *
 * - Ensure that only 1 dropdown ever shows at the same time.
 * - Close itself when clicked outside.
 */
export function useDropdownMutex() {
  const context = React.useContext(DropdownMutexContext);
  const [mutexId] = React.useState(context.generateId());

  const isVisible = context.currentId === mutexId;
  const show = () => context.registerId(mutexId);
  const hide = () => context.unregisterId(mutexId);

  return {
    isVisible,
    show,
    hide,
    toggle: () => (isVisible ? hide() : show()),
  };
}
